
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { createCourseEnrollmentCheckout } from '@/lib/stripeConnect';
import { devLog, devError } from '@/utils/devLog';
import { formatPrice } from '@/utils/formatters';

export default function GlobalCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Get course data from URL parameters
  const courseId = searchParams.get('courseId');
  const courseName = searchParams.get('courseName') ? 
    decodeURIComponent(searchParams.get('courseName')!) : 
    'Course';
  const coursePrice = searchParams.get('price') ? 
    parseFloat(searchParams.get('price')!) : 
    0;
    
  // Check if we're in a cancel state
  const isCancel = searchParams.get('cancel') === 'true';
  
  useEffect(() => {
    devLog("[GlobalCheckout] Mounted", {
      courseId,
      courseName,
      coursePrice,
      isCancel
    });
    
    // If this is a cancel state, automatically navigate back
    if (isCancel) {
      handleGoBack();
    }
    
    // Store the referrer as a fallback return path
    if (document.referrer && document.referrer.includes(window.location.origin)) {
      sessionStorage.setItem('checkoutReferrer', document.referrer);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProceedToPayment = async () => {
    if (!courseId || !user) return;
    
    try {
      setProcessingPayment(true);
      
      devLog("[GlobalCheckout] Processing payment for:", {
        courseId,
        userId: user.id,
        price: coursePrice,
        title: courseName
      });
      
      // Get checkout URL from Stripe
      const checkoutUrl = await createCourseEnrollmentCheckout(
        courseId,
        user.id,
        coursePrice,
        courseName
      );
      
      // CRITICAL FIX: Add extensive logging before redirect
      console.log('[GlobalCheckout] Received checkout URL:', checkoutUrl);
      devLog("[GlobalCheckout] About to redirect to Stripe", { 
        hasUrl: !!checkoutUrl,
        urlPrefix: checkoutUrl?.substring(0, 30),
      });
      
      if (!checkoutUrl) {
        throw new Error("No checkout URL returned");
      }
      
      // CRITICAL FIX: Direct window location change for guaranteed redirect
      console.log('[GlobalCheckout] Redirecting to Stripe checkout URL now...');
      
      // Use setTimeout to ensure the logs are visible before navigation
      setTimeout(() => {
        window.location.href = checkoutUrl;
      }, 100);
      
    } catch (error: any) {
      devError("[GlobalCheckout] Payment processing error:", error);
      toast.error(error.message || "Failed to process payment");
      setProcessingPayment(false);
    }
  };

  const handleGoBack = () => {
    // Try the saved referrer in session storage first
    const savedReferrer = sessionStorage.getItem('checkoutReferrer');
    if (savedReferrer) {
      window.location.href = savedReferrer;
      return;
    }
    
    // Default fallback to home
    navigate('/');
  };

  if (!courseId || !courseName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-md p-8">
          <h1 className="text-2xl font-bold mb-4">Course not found</h1>
          <p className="text-muted-foreground mb-6">
            We couldn't find the course information. Please try again or contact support.
          </p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container px-4 mx-auto max-w-3xl">
        <Card className="p-6 shadow-lg">
          {loading ? (
            <div className="w-full max-w-md p-8 space-y-4">
              <Skeleton className="h-8 w-64 mb-4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-12 w-full mt-8" />
            </div>
          ) : (
            <div className="space-y-6">
              <h1 className="text-2xl font-bold">Complete Your Purchase</h1>
              <div className="border-t border-b py-4">
                <h2 className="font-medium mb-2">Order Summary</h2>
                <div className="flex justify-between items-center">
                  <span>{courseName}</span>
                  <span className="font-bold">${formatPrice(coursePrice)}</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center font-bold text-lg">
                <span>Total:</span>
                <span>${formatPrice(coursePrice)}</span>
              </div>
              
              {/* CRITICAL FIX: Add ID to button for debugging */}
              <Button 
                id="stripe-checkout-button"
                onClick={handleProceedToPayment} 
                className="w-full"
                size="lg"
                disabled={processingPayment}
              >
                {processingPayment ? (
                  <>Processing...</>
                ) : (
                  <>Proceed to Payment</>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleGoBack}
                className="w-full mt-2"
              >
                Go Back
              </Button>

              {/* Add debug panel in development */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-8 p-4 border border-dashed border-gray-300 bg-gray-50 rounded text-xs">
                  <h3 className="font-mono font-bold">Debug Info:</h3>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify({
                      courseId,
                      courseTitle: courseName, 
                      coursePrice,
                      path: window.location.pathname,
                      searchParams: Object.fromEntries(searchParams.entries())
                    }, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
